import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

import { trackClickForSegmentAnalytics } from '@infura/packages.common/segmentAnalytics'
import { Button } from '@infura/packages.ui/Button'
import { Loader } from '@infura/packages.ui/Loader'

import { useElementHeight } from 'lib/dom'
import { LoginProvider, useWeb3Auth } from 'lib/web3auth'

type Props = {
  onError?: (error: string) => void
}

const BUTTON_HEIGHT = 47
const GAP = 8
const INITIAL_HEIGHT = BUTTON_HEIGHT + GAP
const ICON_SIZE = 16
const SHOW_VIEW_MORE_BUTTON = false

export const SocialAuthForm = ({ onError }: Props) => {
  const { authenticate, error, loginProvider, ready, submitting } = useWeb3Auth()
  const [isExpanded, setIsExpanded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const height = useElementHeight(containerRef)
  const expandedHeight = isExpanded ? `${height}px` : `${INITIAL_HEIGHT}px`

  useEffect(() => {
    if (error) onError?.(error)
  }, [error, onError])

  if (!ready) return <Skeleton />

  const handleSocialButtonClick = (provider: { provider: LoginProvider; name: string }) => {
    authenticate(provider.provider)
    trackClickForSegmentAnalytics({
      clickText: getAriaLabel(provider.name),
      location: 'Social Auth Form',
      type: 'Button'
    })
  }

  return (
    <div className='w-full'>
      <div className='flex flex-col gap-4'>
        <div
          className='grid grid-cols-[repeat(auto-fit,minmax(104px,1fr))] gap-2 overflow-hidden transition-[height] duration-300 ease-in-out'
          ref={containerRef}
          style={{ height: expandedHeight }}
        >
          {supportedProviders.map(provider => (
            <div className='flex items-center justify-center' key={provider.provider}>
              <Button
                aria-label={getAriaLabel(provider.name)}
                className='flex h-[47px] items-center justify-center'
                fullWidth
                loading={loginProvider === provider.provider ? submitting : false}
                onClick={() => handleSocialButtonClick(provider)}
                preserveTextWhileLoading={false}
                theme='secondary'
              >
                <Image alt={provider.name} height={ICON_SIZE} src={provider.icon} width={ICON_SIZE} />
              </Button>
            </div>
          ))}
        </div>
      </div>
      {SHOW_VIEW_MORE_BUTTON ? (
        <div className='flex justify-end'>
          <Button
            className='text-primary-default h-[20px] !px-0 !py-0 text-sm'
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
            theme={'tertiary'}
            type='button'
          >
            {isExpanded ? 'View less' : 'View more'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

const getAriaLabel = (providerName: string) => {
  return `Sign in with ${providerName}`
}

const Skeleton = () => {
  return (
    <div className='mx-auto flex w-full flex-col gap-4'>
      <Loader className='h-12' pulse />
    </div>
  )
}

const supportedProviders: Array<{
  icon: string
  name: string
  provider: LoginProvider
}> = [
  {
    icon: '/images/auth/google.svg',
    name: 'Google',
    provider: 'google'
  },
  {
    icon: '/images/auth/github.svg',
    name: 'GitHub',
    provider: 'github'
  },
  {
    icon: '/images/auth/apple.svg',
    name: 'Apple',
    provider: 'apple'
  }
] as const
